import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

// First-come-first-served claim helper.
//
// Two flavours supported by the same hook:
//   • Single-lead pages (Lead Detail, Pipeline drawer) pass `lead` at
//     init — they get the status flags (locked / ownerSelf / etc.)
//     for rendering ownership badges AND a zero-arg `requestClaim()`.
//   • List pages (Leads table, Customers table) skip `lead` at init
//     and call `requestClaimFor(lead, onProceed)` per-row.
//
// In both flavours one shared confirm modal hangs in the consumer's
// render tree via `confirmProps`. The mutation invalidates lead /
// conversations / leads queries on success so the new ownership
// shows up immediately wherever the lead is rendered.
export default function useLeadClaim(opts = {}) {
  // `noun`: what the confirm dialog calls the record ("lead", "customer").
  const { lead, currentUser, noun = 'lead' } = opts;
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(null); // { onConfirm }

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // `assignedTo` is stored as ObjectId; the populated user lives on
  // `assignedUser` (set by GET /leads/:id) — read id from one, label
  // from the other.
  function ownerInfoFor(l) {
    const ownerId = l?.assignedTo?._id || l?.assignedTo || null;
    const ownerSelf = ownerId && currentUser?._id && String(ownerId) === String(currentUser._id);
    const ownerName =
      l?.assignedUser?.name ||
      l?.assignedUser?.email ||
      l?.assignedTo?.name ||
      l?.assignedTo?.email ||
      null;
    return {
      ownerId,
      ownerSelf,
      ownerOther: !!ownerId && !ownerSelf,
      unassigned: !ownerId,
      ownerName,
    };
  }

  const claimMutation = useMutation(
    (leadId) => api.post(`/leads/${leadId}/claim`).then((r) => r.data),
    {
      onSuccess: (_data, leadId) => {
        queryClient.invalidateQueries(['lead', String(leadId)]);
        queryClient.invalidateQueries(['conversations']);
        queryClient.invalidateQueries(['leads']);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Could not claim lead');
      },
    }
  );

  // Core gate. Operator-confirmed claim flow per lead.
  const requestClaimFor = (l, onProceed) => {
    if (!l?._id) {
      if (onProceed) onProceed();
      return;
    }
    const info = ownerInfoFor(l);
    if (info.ownerOther && !isAdmin) {
      toast.error(`Lead is assigned to ${info.ownerName || 'another operator'}`);
      return;
    }
    if (info.unassigned) {
      setPending({
        onConfirm: () => {
          claimMutation.mutate(l._id, {
            onSuccess: () => { if (onProceed) onProceed(); },
            onError: () => {},
          });
        },
      });
      return;
    }
    if (onProceed) onProceed();
  };

  // Single-lead convenience API (LeadDetail / PipelineLeadDrawer).
  const info = lead ? ownerInfoFor(lead) : {};
  const requestClaim = (onProceed) => requestClaimFor(lead, onProceed);

  return {
    isAdmin,
    ...info,
    locked: !!info.ownerOther && !isAdmin,
    requestClaim,
    requestClaimFor,
    confirmProps: {
      isOpen: !!pending,
      onClose: () => setPending(null),
      onConfirm: () => {
        const fn = pending?.onConfirm;
        setPending(null);
        if (fn) fn();
      },
      title: `Claim this ${noun}?`,
      message: `This action will assign the ${noun} to you. Other operators will only be able to view it; admins can reassign if needed. Continue?`,
      confirmText: 'Yes, claim',
      isLoading: claimMutation.isLoading,
    },
  };
}
